'use client'

import { useState } from 'react'
import { X } from 'lucide-react'
import type { ReviewCardData } from '../content/reviewTypes'
import { StarRating } from './StarRating'
import { ReviewerAvatar } from './ReviewerAvatar'
import { GoogleMark } from './GoogleMark'
import { formatAverageOutOfFive, formatRelativeReviewDate, truncateReviewText } from './reviewsFormat'

export type ReviewHubPanelProps = {
  reviews: ReviewCardData[]
  average: number
  count: number
  /** Dealer name for the branded header band (tenant.name). */
  companyName: string
  onClose: () => void
}

/**
 * The reviews hub panel — a non-blocking `role="dialog"` (`aria-modal={false}`)
 * that lists real reviews under a branded header and an aggregate summary.
 * Purely presentational: the host owns open state, Esc, and outside-click.
 * Content is `cached_reviews` only (no-fabrication); the only authored strings
 * here are the "reviews & testimonials" label and the two control labels.
 *
 * READ-ONLY BY DESIGN. This panel deliberately carries no "Leave a review"
 * CTA. A visitor who opened it is reading what other people said before
 * deciding to book; asking them to go write a review is aimed at a different
 * person entirely (a past customer), and it sends them off to Google mid-
 * consideration. The ask belongs on the surfaces built for it — the
 * `wall_of_love` / `reviews_feed` / `reviews_summary` sections, which have
 * their own dealer-toggled CTA — not on the floating layer. Do not re-add it
 * here without that reasoning changing.
 *
 * The header band FILLS with `--color-secondary` (labels on it read
 * `--color-secondary-text`), so the panel is unmistakably the dealer's. The
 * aggregate figures PAINT TYPE, so they read `--color-secondary-readable` —
 * the fill-vs-foreground rule that `--color-cta` / `--color-cta-readable`
 * carries. Raw `--color-secondary` as text is 2.16:1 on Adventure Elevated.
 *
 * Stars stay `--color-star` gold rather than any brand color: a gold star is
 * the cross-web signal for "rating", and a brand-tinted one stops reading as
 * one. See tokens.css.
 *
 * `tabIndex={-1}` makes the dialog itself a valid, non-tab-order focus
 * target — the host focuses it on open so a control that unmounts when the
 * panel appears (the teaser card) doesn't drop keyboard focus to `<body>`.
 */
export function ReviewHubPanel({ reviews, average, count, companyName, onClose }: ReviewHubPanelProps) {
  // Which review bodies the visitor has expanded, by id. Expansion is one-way:
  // having asked for the rest of a review, nobody then wants it re-clipped, and
  // a re-collapse would shift every row below it out from under their cursor.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())

  return (
    <div
      role="dialog"
      aria-modal={false}
      aria-label="Customer reviews"
      tabIndex={-1}
      className="flex max-h-[min(32rem,78vh)] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
    >
      <div className="flex items-center justify-between gap-2 bg-[var(--color-secondary)] px-4 py-3">
        <span
          className="min-w-0 truncate text-sm font-semibold text-[var(--color-secondary-text)]"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {companyName}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close reviews"
          className="-mr-1 shrink-0 rounded-md p-1 text-[var(--color-secondary-text)] opacity-80 transition-opacity hover:opacity-100"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>

      {/* Aggregate: rating and volume as two peer figures either side of a
        * rule, rather than one stacked block. They answer different questions
        * ("how good?" / "how many say so?") and the split stops the count
        * reading as a footnote to the score. */}
      <div className="grid grid-cols-2 items-center divide-x divide-[var(--color-border)] border-b border-[var(--color-border)] px-2 py-4">
        <div className="flex flex-col items-center gap-1.5 px-2">
          <span
            className="text-3xl leading-none font-bold text-[var(--color-secondary-readable)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {formatAverageOutOfFive(average)}
          </span>
          <StarRating rating={average} className="text-lg" />
        </div>
        <div className="flex flex-col items-center gap-1.5 px-2 text-center">
          <span
            className="text-3xl leading-none font-bold text-[var(--color-secondary-readable)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {count}
          </span>
          <span className="text-xs leading-snug font-semibold text-[var(--color-text-secondary)]">
            reviews &amp; testimonials
          </span>
        </div>
      </div>

      <ul className="flex-1 divide-y divide-[var(--color-border)] overflow-y-auto">
        {reviews.map((r) => {
          const isExpanded = expanded.has(r.id)
          const { preview, truncated } = truncateReviewText(r.review_text)
          const relative = r.review_date ? formatRelativeReviewDate(r.review_date) : ''
          return (
            <li key={r.id} className="px-4 py-4">
              <div className="flex items-start gap-3">
                <ReviewerAvatar src={r.reviewer_avatar} name={r.reviewer_name} sizeClass="size-11 text-lg" />
                <div className="min-w-0 flex-1">
                  <span
                    className="block truncate text-sm font-semibold text-[var(--color-text-primary)]"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {r.reviewer_name}
                  </span>
                  <StarRating rating={r.rating} className="mt-0.5 text-sm" />
                  <p
                    className="mt-1.5 text-sm leading-relaxed text-[var(--color-text-secondary)]"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    {isExpanded ? r.review_text : preview}
                    {truncated && !isExpanded && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => setExpanded((prev) => new Set(prev).add(r.id))}
                          className="align-baseline rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] font-semibold tracking-wide whitespace-nowrap text-[var(--color-text-muted)] uppercase transition-colors hover:border-[var(--color-border-emphasis)] hover:text-[var(--color-text-secondary)]"
                        >
                          Read more
                        </button>
                      </>
                    )}
                  </p>
                  {(relative || r.source_platform === 'google') && (
                    <div className="mt-2 flex items-center justify-between gap-2">
                      {/* The empty span keeps the source mark right-aligned
                        * when a review carries no usable date. */}
                      {relative ? (
                        <time dateTime={r.review_date ?? undefined} className="text-xs text-[var(--color-text-muted)]">
                          {relative}
                        </time>
                      ) : (
                        <span />
                      )}
                      {r.source_platform === 'google' && (
                        <GoogleMark className="size-4 shrink-0 opacity-90" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
